/** Inline click-to-edit description field — blur/Enter saves, Esc cancels */

import { useState, useRef, useEffect, useCallback } from 'react'
import { cn } from '../../lib/utils'

const MAX_LENGTH = 1000
const COUNTER_THRESHOLD = 100 // show counter when within this many chars of limit

interface DescriptionEditorProps {
  value: string | null
  isDark: boolean
  onSave: (description: string | null) => void
  /** True while save mutation is in-flight */
  saving?: boolean
}

export function DescriptionEditor({ value, isDark, onSave, saving }: DescriptionEditorProps) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value ?? '')
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Sync external value changes (e.g. optimistic rollback)
  useEffect(() => {
    if (!editing) setDraft(value ?? '')
  }, [value, editing])

  // Auto-focus + resize on enter edit
  useEffect(() => {
    if (editing && textareaRef.current) {
      const el = textareaRef.current
      el.focus()
      el.selectionStart = el.selectionEnd = el.value.length
      autoResize(el)
    }
  }, [editing])

  function autoResize(el: HTMLTextAreaElement) {
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }

  const handleSave = useCallback(() => {
    const trimmed = draft.trim()
    const next = trimmed.length === 0 ? null : trimmed
    // Only save if changed
    if (next !== (value ?? null)) onSave(next)
    setEditing(false)
  }, [draft, value, onSave])

  const handleCancel = useCallback(() => {
    setDraft(value ?? '')
    setEditing(false)
  }, [value])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSave()
    } else if (e.key === 'Escape') {
      handleCancel()
    }
  }

  const charsLeft = MAX_LENGTH - draft.length
  const showCounter = charsLeft <= COUNTER_THRESHOLD

  const placeholderText = '+ Add description'
  const mutedText = isDark ? 'text-neutral-500' : 'text-neutral-400'
  const bodyText = isDark ? 'text-neutral-300' : 'text-neutral-700'
  const borderFocus = isDark ? 'border-brand-500/50 bg-surface-3' : 'border-brand-500 bg-neutral-50'

  if (editing) {
    return (
      <div className="px-2 pb-2">
        <textarea
          ref={textareaRef}
          value={draft}
          onChange={(e) => { setDraft(e.target.value.slice(0, MAX_LENGTH)); autoResize(e.target) }}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          rows={2}
          maxLength={MAX_LENGTH}
          aria-label="File description"
          aria-multiline="true"
          className={cn(
            'w-full resize-none rounded border px-1.5 py-1 text-[10px] leading-relaxed outline-none transition-colors',
            bodyText,
            borderFocus,
          )}
        />
        {showCounter && (
          <p className={cn('mt-0.5 text-right text-[9px]', charsLeft <= 20 ? 'text-red-400' : mutedText)}>
            {charsLeft} left
          </p>
        )}
      </div>
    )
  }

  return (
    <button
      onClick={() => setEditing(true)}
      disabled={saving}
      aria-label={value ? 'Edit description' : 'Add description'}
      className={cn(
        'w-full px-2 pb-2 text-left text-[10px] leading-relaxed transition-colors',
        value ? bodyText : mutedText,
        !saving && (isDark ? 'hover:text-neutral-200' : 'hover:text-neutral-900'),
      )}
    >
      {value ?? placeholderText}
    </button>
  )
}
